// src/tabs/messages/MessagesPreviewColumn.jsx — LOT P2 (refonte visuelle
// Plateformes) : APERÇU en 3e colonne persistante, façon maquette
// docs/handoff/maquette-plateformes-fbz.html (colonne droite).
//
// - Source : « Exemple générique » (fausse résa d'exemple ci-dessous + premier
//   logement réel de la cfg — plus besoin d'une résa en base) OU une résa
//   réelle (30 dernières, lecture seule — même requête que l'aperçu
//   historique de MessageEditor).
// - Toggle Airbnb/Booking : surcharge UNIQUEMENT le champ `source` du contexte
//   d'aperçu → les encarts {#if is_airbnb}/{#if is_booking} s'affichent ou non
//   (renderTemplate/evaluateConditions existants — pur ajout d'UI, ADR-266 :
//   pas d'option Direct ici, Plateformes = Airbnb/Booking).
// - Le contenu aperçu = le BROUILLON VIVANT de MessageEditor (prop `draft`
//   remontée par onPreviewChange) — même liveness que l'aperçu historique.
// LECTURE SEULE STRICTE : aucune écriture, aucun send touché.

import { useEffect, useMemo, useState } from "react"
import { Eye, Mail } from "lucide-react"
import { supabase } from "../../supabase"
import { DS } from "../../lib/ui"
import { getAllLogsFlat } from "../../lib/sousloc-helpers"
import { renderTemplate } from "../../lib/template-variables"
import { withPreviewLinkPlaceholders } from "./preview-link-placeholders"

// Résa d'exemple (mode générique) — valeurs alignées sur les `example:` du
// catalogue de variables (template-variables.js). `source` est posé par le
// toggle au rendu.
export const EXAMPLE_RESA = {
  id: "exemple",
  guest_first_name: "Sophie",
  guest_last_name: "Martin",
  guest_email: "sophie.martin@example.fr",
  guest_phone: "+33 6 12 34 56 78",
  guest_language: "fr",
  guest_count: 2,
  start_date: "2026-05-05",
  end_date: "2026-05-08",
  total_amount: 184,
  cleaning_fee: 40,
  ota_chat_link: "https://airbnb.com/exemple",
}

const PLATS = [
  { id: "airbnb", label: "Airbnb" },
  { id: "booking", label: "Booking" },
]

// `plats` (lot visuel n°2) : plateformes sur lesquelles le message OUVERT peut
// réellement partir (union des conditions source de ses envois, calculée par
// le parent via allowedOtaPlatforms — LECTURE SEULE). Seuls ces toggles sont
// affichés ; 1 seule → simple étiquette (pas un choix mort). MAJ au refresh
// de la liste (pas à la frappe) — assumé Dimitri.
export default function MessagesPreviewColumn({ cfg, draft, plats = ["airbnb", "booking"] }) {
  const [plat, setPlat] = useState("airbnb")

  // Réalignement : si la plateforme sélectionnée sort de l'ensemble autorisé
  // (ex. message Booking-only ouvert alors que l'aperçu était sur Airbnb).
  useEffect(() => {
    if (!plats.includes(plat)) setPlat(plats[0] || "airbnb")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plats.join(","), plat])
  const [sourceId, setSourceId] = useState("exemple")
  const [resas, setResas] = useState([])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from("reservations")
        .select("*")
        .order("start_date", { ascending: false })
        .limit(30)
      if (!cancelled) setResas(data || [])
    })()
    return () => { cancelled = true }
  }, [])

  // getAllLogsFlat : descend dans les chambres sous-loc (cf. anti-pattern
  // KV ↔ V2, CLAUDE.md) — sinon variables logement vides sur lc1/lc2/lc3.
  const allLogs = useMemo(() => getAllLogsFlat(cfg), [cfg])

  const realResa = sourceId !== "exemple" ? resas.find(r => r.id === sourceId) || null : null
  const baseResa = realResa || EXAMPLE_RESA
  // Toggle plateforme : surcharge du champ source pour l'APERÇU seulement.
  const resa = { ...baseResa, source: plat }
  const logement = realResa
    ? allLogs.find(l => l.id === realResa.logement_id) || null
    : allLogs[0] || null

  const lang = (draft && draft.activeLang) || "fr"
  // Liens générés à l'envoi (jeton) : placeholder honnête en aperçu — sinon
  // {livret_url}/{welcome_form_url}/{paiement_caution_url} rendent VIDE ici.
  const ctx = withPreviewLinkPlaceholders({ reservation: resa, logement, cfg, language: lang })
  const subject = draft ? renderTemplate(draft.tpl["subject_" + lang] || "", ctx) : ""
  const body = draft ? renderTemplate(draft.tpl["body_" + lang] || "", ctx) : ""

  return (
    <div data-testid="msg-preview-col"
      style={{background:DS.card,border:`0.5px solid ${DS.border}`,borderRadius:DS.radiusCard,boxShadow:"0 10px 30px -14px rgba(20,51,80,.22)",overflow:"hidden"}}>
      <div style={{padding:"12px 14px",borderBottom:`1px solid ${DS.border}`,display:"flex",flexDirection:"column",gap:9}}>
        <div style={{display:"flex",alignItems:"center",gap:7,fontSize:11,fontWeight:700,color:DS.marine,textTransform:"uppercase",letterSpacing:.6}}>
          <Eye size={14} strokeWidth={2}/> Aperçu
        </div>
        {/* Source : Exemple générique | résa réelle (lecture seule) */}
        <select data-testid="msg-preview-source" value={sourceId} onChange={e => setSourceId(e.target.value)}
          style={{border:`0.5px solid ${DS.border}`,borderRadius:DS.radiusControl,padding:"6px 9px",fontSize:11.5,outline:"none",background:DS.card,fontFamily:DS.fontBody,color:DS.textPrimary}}>
          <option value="exemple">Exemple générique (Sophie Martin · 5→8 mai)</option>
          {resas.map(r => (
            <option key={r.id} value={r.id}>
              {(r.guest_first_name || "?") + " " + (r.guest_last_name || "")} · {r.logement_id} · {r.start_date}
            </option>
          ))}
        </select>
        {/* Toggle plateforme — les encarts {#if is_airbnb}/{#if is_booking}
            suivent. Pas de « Direct » (étanchéité ADR-266). FILTRÉ aux
            plateformes possibles du message (lot visuel n°2) ; une seule →
            étiquette statique. */}
        {plats.length > 1 ? (
          <div data-testid="msg-preview-plat" style={{display:"flex",background:DS.pageBg,borderRadius:9,padding:3,gap:2}}>
            {PLATS.filter(p => plats.includes(p.id)).map(p => {
              const on = plat === p.id
              return (
                <button key={p.id} type="button" data-testid={`msg-preview-plat-${p.id}`}
                  onClick={() => setPlat(p.id)}
                  style={{flex:1,border:"none",background:on?DS.card:"transparent",color:on?DS.marine:DS.textMuted,boxShadow:on?"0 1px 3px rgba(20,51,80,.14)":"none",borderRadius:6,padding:"5px 8px",fontFamily:DS.fontBody,fontSize:11.5,fontWeight:600,cursor:"pointer"}}>
                  {p.label}
                </button>
              )
            })}
          </div>
        ) : (
          <div data-testid="msg-preview-plat-single"
            style={{display:"flex",alignItems:"center",gap:6,fontSize:11.5,fontWeight:600,color:DS.marine,background:DS.pageBg,borderRadius:9,padding:"6px 10px"}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:plats[0]==="airbnb"?DS.corail:DS.marine,flexShrink:0}}/>
            {PLATS.find(p => p.id === plats[0])?.label} <span style={{fontWeight:400,color:DS.textMuted}}>· seule plateforme possible pour ce message</span>
          </div>
        )}
      </div>

      {!draft && (
        <div data-testid="msg-preview-empty" style={{padding:"36px 18px",textAlign:"center",color:DS.textMuted,fontSize:12.5}}>
          Sélectionnez un message pour voir ce que reçoit le destinataire.
        </div>
      )}

      {draft && (
        <div style={{padding:14}}>
          {/* Carte email brandée (maquette : cover + eyebrow Sujet + corps) */}
          <div style={{border:`0.5px solid ${DS.border}`,borderRadius:12,overflow:"hidden"}}>
            <div style={{background:DS.marineHero,color:"#fff",padding:"13px 15px"}}>
              <div style={{fontSize:9,fontWeight:700,letterSpacing:1,textTransform:"uppercase",opacity:.75,display:"flex",alignItems:"center",gap:5,marginBottom:4}}>
                <Mail size={11} strokeWidth={2}/> Sujet · {plat === "airbnb" ? "Airbnb" : "Booking"} · {lang.toUpperCase()}
              </div>
              <div data-testid="msg-preview-subject" style={{fontFamily:DS.fontDisplay,fontSize:14.5,fontWeight:600,lineHeight:1.35}}>
                {subject || <span style={{opacity:.6,fontStyle:"italic"}}>(sujet vide pour {lang.toUpperCase()})</span>}
              </div>
            </div>
            <div data-testid="msg-preview-body"
              style={{padding:"14px 15px",fontSize:12.5,color:DS.textPrimary,lineHeight:1.65,whiteSpace:"pre-wrap",fontFamily:DS.fontBody,background:DS.card}}>
              {body || <span style={{color:DS.textMuted,fontStyle:"italic"}}>(corps vide pour {lang.toUpperCase()})</span>}
            </div>
          </div>
          <div style={{fontSize:10,color:DS.textMuted,marginTop:8,lineHeight:1.5}}>
            Rendu avec {realResa ? "la résa sélectionnée" : "des valeurs d'exemple"} — les variables sans valeur restent en {"{accolades}"}.
          </div>
        </div>
      )}
    </div>
  )
}
